import { TextField as CMSTextField } from '@cmsgov/design-system';
import classnames from 'classnames';
import type * as React from 'react';

import { ErrorIcon, InfoIcon } from '../Icon';
import Tooltip from '../Tooltip';
import { REQUIRED_STATE_TEXT } from '../RequiredStateText';
import './TextField.css';

interface TextFieldProps {
  /**
   * Label for the text field
   */
  label?: string;
  /**
   * Name attribute for the text field
   */
  name: string;
  /**
   * Default value for the text field
   */
  defaultValue?: string;
  /**
   * Value for controlled component
   */
  value?: string;
  /**
   * Callback when text field value changes
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /**
   * Callback when text field loses focus
   */
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /**
   * Additional class name
   */
  className?: string;
  /**
   * Whether the text field is disabled
   */
  disabled?: boolean;
  /**
   * Whether the field is read-only (displays value without input)
   */
  readOnly?: boolean;
  /**
   * Hint text to display below the label
   */
  hint?: string;
  /**
   * Error message to display
   */
  errorMessage?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Tooltip info text to display next to the label
   */
  tooltipText?: string;
  /**
   * Type of input
   */
  type?: 'text' | 'email' | 'number' | 'password' | 'tel' | 'url';
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Maximum length
   */
  maxLength?: number;
  /**
   * Callback when key is pressed
   */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Whether to render as a multiline textarea
   */
  multiline?: boolean;
  /**
   * Number of rows for textarea (only applies when multiline is true)
   */
  rows?: number;
  /**
   * Autocomplete attribute for browser autofill
   */
  autoComplete?: string;
  /**
   * Icon element to render inside the text field on the right side.
   * Useful for toggle buttons like show/hide password.
   */
  rightIcon?: React.ReactNode;
  /**
   * Callback when the right icon is clicked
   */
  onRightIconClick?: () => void;
  /**
   * Aria label for the right icon button
   */
  rightIconAriaLabel?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function TextField({
  label,
  name,
  defaultValue,
  value,
  onChange,
  onBlur,
  className,
  disabled,
  readOnly,
  hint,
  errorMessage,
  required,
  tooltipText,
  type = 'text',
  placeholder,
  maxLength,
  onKeyDown,
  autoComplete,
  multiline = false,
  rows = 5,
  rightIcon,
  onRightIconClick,
  rightIconAriaLabel,
  'data-testid': dataTestId,
}: TextFieldProps) {
  const hasError = !!errorMessage;
  // ACP-2092: JAWS does not speak aria-required, so the state is part of the announced
  // name. aria-label wins over the <label for>, so it has to be composed here rather
  // than added to the label subtree. See RequiredStateText.
  const accessibleName = label ? (required ? `${label} ${REQUIRED_STATE_TEXT}` : label) : name;
  const hasRightIcon = !!rightIcon;
  const baseTestId = dataTestId ?? 'text-field';
  const textFieldClasses = classnames('access-textfield', className, {
    'access-textfield--error': hasError,
    'access-textfield--has-right-icon': hasRightIcon,
    'access-textfield--multiline': multiline,
    'access-textfield--readonly': readOnly,
  });

  // Generate unique IDs for ARIA attributes
  const errorId = `${name}-error`;
  const tooltipId = `${name}-tooltip`;

  if (readOnly) {
    return (
      <div
        className={textFieldClasses}
        data-testid={baseTestId}>
        <div
          className='access-textfield__label-wrapper'
          data-testid={`${baseTestId}-label`}>
          {label && (
            <label className='ds-c-label'>
              <span className='access-textfield__label-content'>
                {label}
                {tooltipText && (
                  <span className='access-textfield__label-suffix'>
                    <Tooltip
                      className='access-textfield__info-tooltip'
                      id={tooltipId}
                      placement='top'
                      title={tooltipText}>
                      <InfoIcon />
                    </Tooltip>
                  </span>
                )}
              </span>
            </label>
          )}
        </div>
        <div
          className='access-textfield__readonly-value'
          data-testid={`${baseTestId}-readonly-value`}>
          <p className='ds-u-margin--0 ds-u-color--muted'>{value || defaultValue || 'Not provided'}</p>
        </div>
      </div>
    );
  }

  // Render editable field
  return (
    <div
      className={textFieldClasses}
      data-testid={baseTestId}
      role='group'>
      <div
        className='access-textfield__label-wrapper'
        data-testid={`${baseTestId}-label`}>
        {label && (
          <label
            className='ds-c-label'
            htmlFor={name}>
            <span className='access-textfield__label-content'>
              {label}
              {(required || tooltipText) && (
                <span className='access-textfield__label-suffix'>
                  {required && (
                    <span
                      aria-hidden='true'
                      className='access-textfield__required-asterisk'>
                      *
                    </span>
                  )}
                  {tooltipText && (
                    <Tooltip
                      className='access-textfield__info-tooltip'
                      id={tooltipId}
                      placement='top'
                      title={tooltipText}>
                      <InfoIcon />
                    </Tooltip>
                  )}
                </span>
              )}
            </span>
          </label>
        )}
      </div>
      <div
        className='access-textfield__field-wrapper'
        data-testid={`${baseTestId}-field`}>
        {multiline ? (
          <textarea
            aria-describedby={errorMessage ? errorId : undefined}
            aria-invalid={hasError}
            aria-label={accessibleName}
            aria-required={required}
            autoComplete={autoComplete}
            className='ds-c-field access-textfield__textarea'
            data-testid={`${baseTestId}-textarea`}
            defaultValue={defaultValue}
            disabled={disabled}
            id={name}
            maxLength={maxLength}
            name={name}
            placeholder={placeholder}
            rows={rows}
            value={value}
            onBlur={onBlur as (event: React.FocusEvent<HTMLTextAreaElement>) => void}
            onChange={onChange as (event: React.ChangeEvent<HTMLTextAreaElement>) => void}
          />
        ) : (
          <div
            className='access-textfield__input-container'
            data-testid={`${baseTestId}-input-container`}>
            <CMSTextField
              aria-describedby={errorMessage ? errorId : undefined}
              aria-invalid={hasError}
              aria-label={accessibleName}
              aria-required={required}
              autoComplete={autoComplete}
              data-testid={`${baseTestId}-input`}
              defaultValue={defaultValue}
              disabled={disabled}
              errorMessage={undefined}
              hint={hint}
              label={undefined}
              maxLength={maxLength}
              name={name}
              placeholder={placeholder}
              type={type}
              value={value}
              onBlur={onBlur as (event: React.FocusEvent<HTMLInputElement>) => void}
              onChange={onChange as (event: React.ChangeEvent<HTMLInputElement>) => void}
              onKeyDown={onKeyDown}
            />
            {rightIcon && (
              <button
                aria-label={rightIconAriaLabel || 'Toggle visibility'}
                className='access-textfield__right-icon-button'
                data-testid={`${baseTestId}-right-icon`}
                tabIndex={0}
                type='button'
                onClick={onRightIconClick}>
                {rightIcon}
              </button>
            )}
          </div>
        )}
        <div
          className='access-textfield__error-icon-wrapper'
          data-testid={`${baseTestId}-error`}>
          {errorMessage ? (
            <>
              <span
                className='sr-only'
                id={errorId}>
                {errorMessage}
              </span>
              <Tooltip
                isError
                activeClassName='access-textfield__error-tooltip--active'
                className='access-textfield__error-icon'
                placement='top'
                title={errorMessage}>
                <span
                  className='access-textfield__error-icon-content'
                  role='img'>
                  <ErrorIcon ariaLabel={`Error: ${errorMessage}`} />
                </span>
              </Tooltip>
            </>
          ) : (
            <span
              aria-hidden='true'
              className='access-textfield__error-icon-placeholder'
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default TextField;
